import * as vp from 'vopay';
import type { SupportedCurrencies } from 'vopay';

import { FunctionComponent, JSX } from 'react';
import { Link } from 'react-router-dom';
import styles from './InterestAccountTransactionTable.module.scss';

const { Box, withStyles, Icon, Button, Tooltip, Table, formattedCurrency } = vp;
const classes = withStyles(styles);

type FC = FunctionComponent<InterestAccountTransactionTableProps>;

type InterestAccountTransactionTableProps = {
  InterestAccountTransactions: vp.InterestAccountTransactionsResponse['InterestAccountTransactions'] | null;
  onInit: () => void;
  currency: SupportedCurrencies;
  formatDate: (param: string) => string;
  getStatusIcon: (iconStatus: string) => JSX.Element | null;
};

interface TransactionData {
  TransactionID: string;
  transactionDate: string;
  transactionType: string;
  debit: string;
  credit: string;
  status: string;
  accountType: string;
}

const InterestAccountTransactionTable: FC = (props: InterestAccountTransactionTableProps) => {
  const { InterestAccountTransactions, currency, formatDate, getStatusIcon, onInit } = props;

  return (
    <>
      <Box className={classes('interestAccountTransactionTableContainer', 'scrollbar')}>
        <Table
          zebra
          id="interestAccountTransactionTable"
          style={{
            '--headerBackground': '#2d2d2d',
            '--headerColor': '#fbfbfb',
            '--headerBorderRadius': '14px',
            '--thBorderBottom': 'none',
          }}
          version="2"
          rows={
            InterestAccountTransactions
              ? InterestAccountTransactions.map(
                  (
                    transaction: vp.InterestAccountTransaction,
                  ): {
                    id: string;
                    data: TransactionData;
                  } => ({
                    id: transaction.TransactionID,
                    data: {
                      TransactionID: transaction.TransactionID,
                      transactionDate: formatDate(transaction.TransactionDate),
                      transactionType: transaction.TransactionType,
                      debit:
                        transaction.DebitAmount === '0.00'
                          ? '-'
                          : `${currency} ${formattedCurrency(transaction.DebitAmount)}`,
                      credit:
                        transaction.CreditAmount === '0.00'
                          ? '-'
                          : `${currency} ${formattedCurrency(transaction.CreditAmount)}`,
                      status: transaction.Status,
                      accountType: transaction.AccountType,
                    },
                  }),
                )
              : []
          }
          columns={[
            {
              id: 'TransactionDate',
              header: 'Date',
              align: 'center',
              field: 'transactionDate',
              style: {
                whiteSpace: 'nowrap',
              },
            },
            {
              id: 'transactionType',
              header: 'Trans. Type',
              align: 'center',
              field: 'transactionType',
            },
            {
              id: 'credit',
              header: 'Credit',
              align: 'center',
              field: 'credit',
              // template: ({ data }) => data.credit === '-' ? '-' : formattedCurrency(data.credit),
              style: {
                whiteSpace: 'nowrap',
              },
            },
            {
              id: 'debit',
              header: 'Debit',
              align: 'center',
              field: 'debit',
              // template: ({ data }) => data.credit === '-' ? '-' : formattedCurrency(data.debit),
              style: {
                whiteSpace: 'nowrap',
              },
            },
            {
              id: 'accountType',
              header: 'Account Type',
              align: 'center',
              field: 'accountType',
            },
            {
              id: 'transactionID',
              header: 'Transaction ID',
              align: 'center',
              template: ({ data }) => (
                <Box className={classes('interestAccountTransactionLink')}>
                  <Link
                    to={{
                      pathname: `/transactions-history/details/${vp.removeWhiteSpaceURI(data.accountType)}/${
                        data.TransactionID
                      }/${currency}`,
                      state: {
                        transactionId: data.TransactionID,
                        hasErrorInfo: data.status === 'failed',
                      },
                    }}
                  >
                    {data.TransactionID}
                  </Link>
                </Box>
              ),
            },
            {
              id: 'status',
              header: 'Status',
              align: 'center',
              template: (transaction) => (
                <div
                  style={{
                    textTransform: 'capitalize',
                    textAlign: 'left',
                    fontWeight: 500,
                    color: transaction.data.status === 'successful' ? '#14AF40' : '#2D2D2DCC',
                  }}
                >
                  <Box ai="center" pe={transaction.data.status !== 'successful' ? 'md' : undefined}>
                    {getStatusIcon(transaction.data.status)}
                    {transaction.data.status}
                  </Box>
                </div>
              ),
            },
          ]}
        />
      </Box>
      {InterestAccountTransactions === null ||
        (InterestAccountTransactions === undefined && (
          <Box flx="col" ai="center" jc="center" className={classes('transactionTableEmptyContainer')}>
            <Box pb="md">
              <Icon color="low-light" icon="documentBulletPoints" size="xxlg" />
            </Box>
            <Box className={classes('moreInfoText')} pb="md">
              Click &apos;Show Data&apos; to show transaction data
              <Box ps="sm" pe="sm">
                <Tooltip
                  borderColor="rgba(45, 45, 45, 0.6)"
                  icon="info"
                  position="top"
                  bgColor="#fbfbfb"
                  information="Currently, we hide data to ensure VoPay Portal's loads faster. We always strive to improve our services. We will continue to improve the portal's experience."
                  popoverClassName={classes('tooltip')}
                />
              </Box>
            </Box>
            <Box pb="md">
              <Button
                size="xlg"
                width="130px"
                label="Show Data"
                borderRadius="10px"
                onClick={() => {
                  onInit();
                }}
              />
            </Box>
          </Box>
        ))}
      {InterestAccountTransactions && InterestAccountTransactions.length === 0 && (
        <Box flx="col" jc="center">
          <Box ai="center" jc="center" pt="lg">
            There are no records to display within this date range
          </Box>
        </Box>
      )}
    </>
  );
};

export default InterestAccountTransactionTable;
